<template>
	<core-layout-content medium>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("create_claim") }}</template>
		</core-layout-headline-and-actions>
		<j-card class="xs-12 wrap bg5 r2 p4">
			<div class="flex wrap">
				<!-- Policy -->
				<div class="xs-12">
					<core-form-input
						:placeholder="$t('search_policy_number')"
						componentName="j-base-multi-select-with-flexible-value"
						class="fs3"
						name="policy"
						:label="$t('policy_number')"
						:subheadline="$t('optional')"
						nameKey="issuerNumber"
						valueKey="id"
						:allowMultiselect="false"
						v-model="form.policies"
						:options="form.policiesOptions"
						@search="onPolicySearch($event)"
					/>
				</div>
			</div>

			<!-- Status -->
			<div class="mt6 xs-12">
				<core-form-input
					componentName="j-base-select"
					name="status"
					:label="$t('status')"
					:subheadline="$t('optional')"
					nameKey="id"
					valueKey="value"
					v-model="form.status"
					:options="options.statuses"
				/>
			</div>
		</j-card>

		<!-- Facts -->
		<j-card
			v-if="!['aeg', 'aeg_uat', 'demo_uat'].includes($store.state.tenantId)"
			class="xs-12 wrap bg5 r2 p4 mt4"
		>
			<add-facts-to-claim @input="form.facts = $event" />
		</j-card>

		<!-- Diagnosis, Operations... codes -->
		<!-- Display only if we have policy selected -->
		<j-card class="xs-12 wrap bg5 r2 p4 mt4" v-if="computedProductId">
			<!-- General Codes -->
			<!-- Diagnosis Codes -->
			<div>
				<div class="fs4 fw7 mb1">{{ $t("diagnosis_code") }}</div>
				<search-general-codes
					codeName="diagnosisSettings"
					:productId="computedProductId"
					v-model="form.diagnosisCodes"
				/>
			</div>
			<!-- Diagnosis Codes -->
			<div class="mt4">
				<div class="fs4 fw7 mb1">{{ $t("operation_code") }}</div>
				<search-general-codes
					codeName="operationSettings"
					:productId="computedProductId"
					v-model="form.operationCodes"
				/>
			</div>

			<!-- Provider Codes -->
			<div class="mt4">
				<div class="fs4 fw7 mb1">{{ $t("provider_code") }}</div>
				<search-provider-codes
					:productId="computedProductId"
					v-model="form.providerCodes"
				/>
			</div>
		</j-card>

		<!-- Buttons -->
		<div class="mt4">
			<j-button
				:isLoading="isLoading"
				@click="handleSubmit"
			>{{ $t("create_claim") }}</j-button>
		</div>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CoreSelectClient from "@/modules/core/components/CoreSelectClient"
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import JDataTable from "@/components/JDataTable/"
import JList from "@/components/JList"
import JBaseInputText from "@/components/JBaseInputText"
import AddFactsToClaim from "../components/AddFactsToClaim"
import SearchGeneralCodes from "../components/SearchGeneralCodes"
import SearchProviderCodes from "../components/SearchProviderCodes"
import debounce from "lodash.debounce"
import get from "lodash.get"
import api from "../api/"

export default {
	components: {
		CoreModal,
		CoreFormInput,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreSelectClient,
		CrmIcon,
		JBaseInputText,
		JButton,
		JCard,
		JDataTable,
		JList,
		AddFactsToClaim,
		SearchGeneralCodes,
		SearchProviderCodes
	},
	data() {
		return {
			isLoading: false,
			isSearching: false,
			form: {
				claimant: null,
				policies: null,
				policiesOptions: [],
				status: null,
				benefitClaims: [],
				facts: [],
				benefitOptions: [],
				diagnosisCodes: [],
				operationCodes: [],
				providerCodes: []
			},
			options: {
				statuses: [
					{
						id: "Approved",
						value: "APPROVED"
					},
					{
						id: "Processing",
						value: "PROCESSING"
					},
					{
						id: "Rejected",
						value: "REJECTED"
					}
				]
			}
		}
	},
	computed: {
		computedPolicy() {
			return this.form?.policies?.[0] || {}
		},
		computedProductId() {
			console.log(this.computedPolicy?.acceptedOffer)
			return this.computedPolicy?.acceptedOffer?.product?.productId || null
		}
	},

	methods: {
		/**
		 * @description IT iwll create claim with benefits and facts
		 */
		async handleSubmit() {
			this.isLoading = true

			// 1: Create Claim
			const createClaim = await api.createClaim({
				createClaimInput: {
					policyId: this.computedPolicy.id || null,
					claimantId: null, //this.form.claimant?.id || null,
					status: this.form?.status || null,
					diagnosisCodes: this.form.diagnosisCodes.map(item => item.code),
					operationCodes: this.form.operationCodes.map(item => item.code),
					providerId: this.form.providerCodes?.[0]?.id || null
				}
			})

			// Check errors
			if (createClaim?.data?.createClaim?.status !== "success") {
				this.$store.dispatch("showError", [this.$t("errors.general")])
				this.isLoading = false
				return
			}

			// Set createdClaimId
			const createdClaimId = createClaim?.data?.createClaim?.createdStatus?.id

			// 2: Add facts to claim
			const isFactsSucces = await this.addFactsToClaim(createdClaimId)
			if (isFactsSucces === false) {
				this.isLoading = false
				return
			}

			// Success
			this.isLoading = false
			this.$router.push({ name: "ClaimDetail", params: { id: createdClaimId } })
		},

		/**
		 * @description This method will add all facts into claim
		 * @param {String} claimId - facts will be added to the claim of this claimId
		 * @returns {Promise} - when all good true otherwise false
		 */
		async addFactsToClaim(claimId) {
			const addedFacts = await Promise.all(
				this.form.facts.map(item =>
					api.addFactToClaim({
						claimId,
						input: {
							type: item.type || null,
							value: { [`${item.dataType.toLowerCase()}Value`]: item.value }
						}
					})
				)
			)

			// Check errors
			if (
				addedFacts.some(
					item => item?.data?.addFactToClaim?.status !== "success"
				)
			) {
				this.$store.dispatch("showError", [this.$t("errors.general")])
				return Promise.resolve(false)
			}

			return Promise.resolve(true)
		},

		onPolicySearch: debounce(async function(searchTerm) {
			this.isSearching = true

			const res = await api.findPolicy({
				limit: 30,
				where: { issuerNumber_contains: searchTerm.toString() }
			})

			if (!res || res.errors || !get(res, "data.policies.totalCount")) {
				// no result
				this.form.policiesOptions = []
				this.isLoading = false
				return
			}

			// show id of policyId
			// and show name of policy &&
			this.form.policiesOptions = get(res, "data.policies.list")
			this.isSearching = false
		}, 500),

		/**
		 * @description When we passs policyId as a query in route, we should prepopulate policy
		 * This flow applies e.g. when user is in PolicyClaims and clicks on Create Claim
		 */
		async prepopulatePolicy() {
			const policyId = this.$route.query.policyId
			if (!policyId) return

			const res = await api.findPolicy({
				where: { id: policyId }
			})

			if (res.errors) return

			const policies = res?.data?.policies?.list || null
			this.form.policies = policies
		}
	},

	created() {
		this.prepopulatePolicy()
	}
}
</script>
